import apiClient from "./apiClient";
import AppError from "../utils/AppError";
import Inspection from "../models/Inspection";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../utils/notifications";
import Frame from "../models/Frame";

const InspectionService = {
  async getInspection(inspectionId) {
    try {
      const { data } = await apiClient.get(`/inspections2/${inspectionId}`);
      return Inspection.fromServerModel(data);
    } catch (error) {
      console.error(error);
      const appError = new AppError({
        title: "Failed to Fetch Inspection",
        response: error.response,
        userMessage: "Could not retrieve the inspection.",
      });
      // The page renders an access-denied view for 403, so a toast would
      // only duplicate it.
      if (error.response?.status !== 403) {
        showErrorNotification(appError);
      }
      throw appError;
    }
  },

  async patchInspection(inspectionId, payload, silent = false) {
    try {
      const { data } = await apiClient.patch(
        `/inspections2/${inspectionId}?use_header_names=true`,
        payload
      );
      if (!silent) {
        showSuccessNotification("Success", "Inspection updated successfully!");
      }
      // The PATCH response is camelCase (chainage, dateCaptured, workOrder, ...),
      // so deserialize with the default (camelCase) mapping. The header-names
      // branch reads chainage from data.LengthSurveyed, which the response does
      // not include, leaving the edited length blank in the panel until reload.
      return Inspection.fromServerModel(data);
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update Inspection",
        response: error.response,
        userMessage: "Could not update the inspection.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getInspections({ organisation, page_size, page, search, ordering }) {
    try {
      let snakeCaseOrdering = ordering;
      if (ordering) {
        const isDescending = ordering.startsWith("-");
        const fieldName = isDescending ? ordering.slice(1) : ordering;
        const snakeField = fieldName.replace(/([A-Z])/g, "_$1").toLowerCase();
        snakeCaseOrdering = isDescending ? `-${snakeField}` : snakeField;
      }

      const { data } = await apiClient.get("/inspections2", {
        params: {
          organisation,
          page_size,
          page,
          search,
          ordering: snakeCaseOrdering,
        },
      });
      const inspections = data.results.map((item) =>
        Inspection.fromServerModel(item)
      );
      return {
        inspections,
        count: data.count,
        statusCounts: data.statusCounts,
      };
    } catch (error) {
      // A 404 means the requested page is out of range (e.g. a persisted page
      // that no longer exists for the current org). Surface the raw error so
      // the caller can snap back to a valid page instead of toasting.
      if (error.response?.status === 404) {
        throw error;
      }
      console.error(error);
      const appError = new AppError({
        title: "Failed to Fetch Inspections",
        response: error.response,
        userMessage: "Could not retrieve inspections list.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async moveInspection(destinationFolderId, inspectionIds) {
    try {
      const payload = { inspectionIds };
      const { data } = await apiClient.patch(
        `/inspections/move/${destinationFolderId}`,
        payload
      );
      showSuccessNotification("Success", "Inspections moved successfully!");
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Move Inspections",
        response: error.response,
        userMessage:
          error?.response?.data?.detail || "Could not move the inspections.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async deleteInspections(inspectionIds) {
    try {
      const payload = { inspectionIds };
      const { data } = await apiClient.delete("/inspections/bulk-delete", {
        data: payload,
      });
      showSuccessNotification("Success", "Inspections deleted successfully!");
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Delete Inspections",
        response: error.response,
        userMessage: "Could not delete the inspections.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  /** Updates the status of a single inspection via the inspection PATCH endpoint. */
  async updateInspectionStatus(inspectionId, status) {
    try {
      const { data } = await apiClient.patch(
        `/inspections2/${inspectionId}?use_header_names=true`,
        { status }
      );
      showSuccessNotification("Success", "Status updated successfully!");
      return Inspection.fromServerModel(data);
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update Status",
        response: error.response,
        userMessage: "Could not update inspection status.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async updateStatus(inspectionIds, status) {
    try {
      const payload = { inspectionIds, status };
      const { data } = await apiClient.post(
        "/inspections/bulk-update-status",
        payload
      );
      showSuccessNotification("Success", "Status updated successfully!");
      return data;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update Status",
        response: error.response,
        userMessage: "Could not update inspection status.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getFrames(inspectionUuid, reportedFrames) {
    try {
      const { data } = await apiClient.get(
        `/inspections2/${inspectionUuid}/frames`,
        {
          params: { reported_frames: reportedFrames },
        }
      );
      const frames = data.map((item) => Frame.fromServerModel(item));
      return frames;
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Fetch Frames",
        response: error.response,
        userMessage: "Could not retrieve frames.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  // DEPRECATED: Use patchObservation instead. Will be removed after migration.
  async patchFrame(frame, payload, silent = false) {
    try {
      Object.keys(payload).forEach((key) => {
        if (payload[key] === "") {
          payload[key] = null;
        }
      });
      // Set default values for quantity units (handles null and undefined)
      if (payload?.quantity1_units == null) {
        payload.quantity1_units = "-";
      }
      if (payload?.quantity2_units == null) {
        payload.quantity2_units = "-";
      }
      const { data } = await apiClient.patch(
        `/inspections/frames/${frame?.id}`,
        payload
      );
      if (!silent) {
        showSuccessNotification("Success", "Frame updated successfully!");
      }
      return {
        patchedFrame: new Frame({
          ...frame,
          ...data,
        }),
        updatedStrGrade: data.inspectionStrGrade,
        updatedSerGrade: data.inspectionSerGrade,
      };
    } catch (error) {
      console.error("Error updating frame:", error);
      const appError = new AppError({
        title: "Failed to Update Frame",
        response: error.response,
        userMessage: "Could not update the frame.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  // DEPRECATED: Use patchObservation from inspectionApi.js instead. Will be removed after migration.
  async updateDefect(frame, inspection, defectId, viewMode, silent = false) {
    try {
      const payload = { defectId, pageContext: { viewMode } };
      const { data } = await apiClient.patch(
        `/inspections/frames/${frame.id}/update-defect`,
        payload
      );
      if (!silent) {
        showSuccessNotification("Success", "Defect code updated successfully!");
      }
      return {
        frame: new Frame({
          ...frame,
          defectId: data.defectId,
          defectClass: data.defectClass,
          defectCode: data.defectCode,
          defectStrScore: data.inspectionStrGrade,
          defectSerScore: data.inspectionSerGrade,
          defectScoreSeverity: data.defectScoreSeverity,
          defectScoreIsShown: data.defectScoreIsShown,
          isHidden: data.isHidden,
          quantity1Value: data.quantity1Value,
          quantity1Units: data.quantity1Units,
          quantity2Value: data.quantity2Value,
          quantity2Units: data.quantity2Units,
        }),
        inspection: new Inspection({
          ...inspection,
          conditionRating: data.inspectionStrGrade,
          serviceConditionRating: data.inspectionSerGrade,
        }),
      };
    } catch (error) {
      const appError = new AppError({
        title: "Failed to Update Defect",
        response: error.response,
        userMessage: "Could not update defect information.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async validateInspection(inspectionIds) {
    try {
      const { data } = await apiClient.post("/inspections/validate", {
        inspectionIds,
      });
      return data;
    } catch (error) {
      console.error("Error validating inspection:", error);
      const appError = new AppError({
        title: "Failed to Validate Inspections",
        response: error.response,
        userMessage: "Could not validate the inspections.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },

  async getShareableLinks(inspectionId) {
    try {
      const { data } = await apiClient.get(
        `/inspections/${inspectionId}/shareable-links`
      );
      return data;
    } catch (error) {
      console.error("Error fetching shareable links:", error);
      const appError = new AppError({
        title: "Failed to Fetch Shareable Links",
        response: error.response,
        userMessage: "Could not retrieve shareable links for this inspection.",
      });
      showErrorNotification(appError);
      throw appError;
    }
  },
};

export default InspectionService;
